/**
 * LoginPanel — split out of AuthHub.jsx in 2026-02.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeSlash, ArrowLeft, EnvelopeSimple, Fingerprint } from "@phosphor-icons/react";
import { setUserToken, userApi, formatUserApiError } from "../../lib/userApi";
import { setAdminToken } from "../../lib/adminApi";
import {
  isWebAuthnSupported, isPlatformAuthenticatorAvailable, loginWithBiometric,
} from "../../lib/webauthnApi";

import {
  GLASS, FieldLabel, TextInput, PasswordInput, DisplayField,
  GoldButton, GhostBackButton, useAuthShellBackground, parseCardDate,
} from "./_shared";

export default function LoginPanel({ onSwitchSignup, onForgotPassword, onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const [bioSupported, setBioSupported] = useState(false);

  // اِكتَشِف إن كان الجِهاز يَدعَم WebAuthn + بِصِمَة حَيَويَّة عَلى المُستَوى المَنَصّيّ
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isWebAuthnSupported()) return;
      const ok = await isPlatformAuthenticatorAvailable();
      if (alive) setBioSupported(ok);
    })();
    return () => { alive = false; };
  }, []);

  // الدُّخول بِالبَصمَة — يَتَطَلَّب أن يَكون المُستَخدِم قَد فَعَّل البَصمَة سابِقاً على هذا الجِهاز
  const onBiometricLogin = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { toast.error("اِكتُب بَريدَك الإلِكترونيّ أَوّلاً"); return; }
    setBioBusy(true);
    try {
      const data = await loginWithBiometric(e);
      if (data.token) setUserToken(data.token);
      await onAuthed({ admin: data.admin });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "تَعَذَّر الدُّخول بِالبَصمَة";
      toast.error(msg);
    } finally {
      setBioBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await userApi
        .post("/auth/login", { email: email.trim().toLowerCase(), password })
        .then((r) => r.data);
      if (data.token) setUserToken(data.token);
      if (data.admin_token) setAdminToken(data.admin_token);
      if (data.restored) {
        toast.success(
          "أَهلاً بِعَودَتِك! تَمَّ استِعادة حسابك بِنَجاح وإلغاء جَدوَلة الحذف.",
          { duration: 6000 },
        );
      }
      await onAuthed({ admin: data.admin });
    } catch (err) {
      toast.error(formatUserApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`${GLASS} p-5 sm:p-6`} data-testid="login-panel">
      <h1 className="text-[22px] font-extrabold text-[#0A192F] leading-tight">تسجيل الدخول</h1>
      <p className="text-[12.5px] text-[#5A6470] mt-1 mb-5">
        ادخل بحساب المحامي، الزائر، أو الإدارة — نفس الصفحة للجميع.
      </p>
      <form onSubmit={onSubmit} className="space-y-3.5">
        <div>
          <FieldLabel>البريد الإلكتروني</FieldLabel>
          <TextInput
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            ltr
            placeholder="name@example.com"
            icon={EnvelopeSimple}
          />
        </div>
        <div>
          <FieldLabel>كلمة المرور</FieldLabel>
          <PasswordInput
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            show={show}
            onToggle={() => setShow((v) => !v)}
          />
          <div className="mt-1.5 text-left">
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={onForgotPassword}
              className="text-[12px] font-bold text-[#A88425] hover:underline"
            >
              نسيت كلمة المرور؟
            </button>
          </div>
        </div>
        <div className="pt-2">
          <GoldButton type="submit" disabled={submitting || !email.trim() || !password} testid="login-submit">
            {submitting ? "جارٍ تسجيل الدخول…" : (<><span>تسجيل الدخول</span><ArrowLeft size={18} weight="bold" /></>)}
          </GoldButton>
        </div>
        {bioSupported && (
          <>
            <div className="relative flex items-center my-1.5" aria-hidden="true">
              <div className="flex-1 h-px bg-[#0A192F]/10" />
              <span className="px-3 text-[11px] font-bold text-[#5A6470] tracking-wider">أَو</span>
              <div className="flex-1 h-px bg-[#0A192F]/10" />
            </div>
            <button
              type="button"
              onClick={onBiometricLogin}
              disabled={bioBusy || !email.trim()}
              data-testid="login-biometric"
              className="w-full h-12 rounded-2xl bg-white border-2 border-[#0A192F]/15 hover:border-[#D4AF37] hover:bg-[#FFFBEA] text-[#0A192F] font-bold text-[15px] inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
            >
              <Fingerprint size={20} weight="duotone" className="text-[#B8860B]" />
              <span>{bioBusy ? "بانتِظار البَصمَة…" : "تَسجيل الدُّخول بِبَصمَة الوَجه أو الإصبَع"}</span>
            </button>
            <p className="text-[11px] text-[#5A6470] text-center mt-1.5">
              اِكتُب بَريدك ثُمَّ اضغط — يَعمَل فَقَط بَعد تَفعيل البَصمَة عَلى هذا الجِهاز
            </p>
          </>
        )}
      </form>
      <p className="text-[13px] text-[#3F4750] text-center mt-5 pt-4 border-t border-white/60">
        ليس لديك حساب؟{" "}
        <button
          type="button"
          data-testid="goto-signup"
          onClick={onSwitchSignup}
          className="text-[#A88425] font-extrabold hover:underline"
        >
          إنشاء حساب جديد
        </button>
      </p>
      {/* رابِط لِلاِطّلاع عَلى الشُّروط وَالخُصوصيّة — مَطلوب لِلامتِثال
          مَع شُروط App Store + Play Store. لا مُوافَقَة هُنا. */}
      <p className="text-[12px] text-[#5A6470] text-center mt-3">
        <Link
          to="/legal"
          data-testid="login-legal-link"
          className="hover:text-[#A88425] hover:underline transition"
        >
          الشُّروط وَسياسَة الخُصوصيّة
        </Link>
      </p>
    </section>
  );
}
